import React, { useEffect, useState } from 'react';
import { apiRequest } from './apiClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// 2026-02 — WhatsApp soft-disable feature flag. When false (default) the
// "Connect your WhatsApp" item is filtered out of the post-login setup
// checklist. Underlying backend item remains intact for one-line
// reactivation via `REACT_APP_WHATSAPP_ENABLED=true`.
const WHATSAPP_ENABLED = String(process.env.REACT_APP_WHATSAPP_ENABLED || 'false').toLowerCase() === 'true';

/**
 * Step 41/45 — Post-login onboarding banner.
 *
 * Renders 5 setup items + an overall counter. Auto-hides when every item
 * is done OR dismissed. The parent wires `onItemClick(itemId)` so the
 * specific in-app navigation per item (Settings tab + section scroll)
 * stays co-located with the view state in App.js.
 */
export default function SetupChecklistBanner({ onItemClick }) {
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  // ENHANCEMENT 2 — WhatsApp guidance modal state. Surfaced when the
  // agent clicks "Set up" on Item 2 ("Connect your WhatsApp"). Forces
  // them to see the ClawHosters prerequisites BEFORE landing on
  // Settings ▸ Integrations (or to manually self-confirm).
  const [showWhatsAppGuide, setShowWhatsAppGuide] = useState(false);

  const refresh = async () => {
    try {
      const { ok, data: payload } = await apiRequest('GET', `${BACKEND_URL}/api/auth/setup-checklist`);
      if (ok) setData(payload);
    } catch { /* banner stays hidden if request fails */ }
  };

  useEffect(() => { refresh(); }, []);

  if (!data || data.all_clear) return null;

  // 2026-02 — When WhatsApp is soft-disabled, strip the "Connect your
  // WhatsApp" item from the rendered checklist (and the counters).
  // Backend list is untouched so reactivating the env flag immediately
  // brings it back.
  const filteredItems = WHATSAPP_ENABLED
    ? data.items
    : data.items.filter(i => i.id !== 'whatsapp');

  const dismissItem = async (itemId) => {
    try {
      await apiRequest('POST', `${BACKEND_URL}/api/auth/setup-checklist/dismiss`, {
        body: { item_id: itemId },
      });
      await refresh();
    } catch { /* noop */ }
  };

  const totalItems = filteredItems.length;
  const doneCount = filteredItems.filter(i => i.done || i.dismissed).length;
  // 2026-02 — If every *visible* (post-flag) item is done or dismissed,
  // hide the banner even when the backend's `all_clear` is still false
  // (the hidden WhatsApp item would otherwise keep the banner up forever).
  if (totalItems > 0 && doneCount >= totalItems) return null;

  return (
    <div
      data-testid="setup-checklist-banner"
      style={{
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: 10,
        padding: '1rem 1.25rem',
        marginBottom: 0,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: collapsed ? 0 : '0.75rem' }}>
        <div style={{ fontSize: '0.95rem', color: '#1e3a8a', fontWeight: 600 }}>
          Finish setting up Estatel AI ({doneCount}/{totalItems} complete)
        </div>
        <button
          onClick={() => setCollapsed(c => !c)}
          style={{
            background: 'transparent', border: 'none', color: '#1e3a8a',
            cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500,
          }}
          data-testid="setup-checklist-toggle"
        >
          {collapsed ? 'Show' : 'Hide'}
        </button>
      </div>

      {!collapsed && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.45rem' }}>
          {filteredItems.map(item => (
            <li
              key={item.id}
              data-testid={`setup-item-${item.id}`}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0.55rem 0.75rem', background: '#fff', borderRadius: 6,
                opacity: item.dismissed ? 0.5 : 1,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
                <span
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 22, height: 22, borderRadius: '50%',
                    background: item.done ? '#16a34a' : (item.dismissed ? '#94a3b8' : '#cbd5e1'),
                    color: '#fff', fontSize: '0.78rem', fontWeight: 700,
                  }}
                  aria-hidden="true"
                >
                  {item.done ? '✓' : (item.dismissed ? '–' : '')}
                </span>
                <span style={{ color: '#0f172a', fontSize: '0.92rem',
                  textDecoration: item.done ? 'line-through' : 'none' }}>
                  {item.label}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.45rem' }}>
                {!item.done && !item.dismissed && (
                  <button
                    type="button"
                    onClick={() => {
                      // ENHANCEMENT 2 — Item 2 "WhatsApp" → guidance
                      // modal first; everything else navigates as before.
                      if (item.id === 'whatsapp') {
                        setShowWhatsAppGuide(true);
                        return;
                      }
                      if (onItemClick) onItemClick(item.id);
                    }}
                    style={{
                      background: '#0073EA', color: '#fff', border: 'none',
                      padding: '0.3rem 0.7rem', borderRadius: 4,
                      fontSize: '0.82rem', fontWeight: 500, cursor: 'pointer',
                    }}
                    data-testid={`setup-item-action-${item.id}`}
                  >
                    Set up
                  </button>
                )}
                {!item.done && !item.dismissed && (
                  <button
                    onClick={() => dismissItem(item.id)}
                    style={{
                      background: '#e2e8f0', color: '#475569', border: 'none',
                      padding: '0.3rem 0.7rem', borderRadius: 4, fontSize: '0.82rem',
                      fontWeight: 500, cursor: 'pointer',
                    }}
                    data-testid={`setup-item-dismiss-${item.id}`}
                  >
                    Not applicable
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* ENHANCEMENT 2 — WhatsApp guidance modal. */}
      {showWhatsAppGuide && (
        <div
          data-testid="whatsapp-guide-modal"
          style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, .55)',
                   display: 'flex', alignItems: 'center', justifyContent: 'center',
                   zIndex: 100 }}
          onClick={() => setShowWhatsAppGuide(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 10,
                     maxWidth: 560, width: '92%',
                     padding: '1.5rem 1.75rem',
                     boxShadow: '0 12px 36px rgba(15,23,42,.22)',
                     color: '#0f172a' }}
          >
            <h2 style={{ marginTop: 0, marginBottom: '0.6rem',
                         fontSize: '1.15rem', color: '#0f172a' }}>
              Connect your WhatsApp
            </h2>
            <p style={{ marginTop: 0, color: '#334155', lineHeight: 1.5 }}>
              To connect WhatsApp you need to set up your Estatel AI bot on
              ClawHosters. Here is what you need:
            </p>
            <ul style={{ paddingLeft: '1.2rem', color: '#334155',
                         lineHeight: 1.55, marginBottom: '0.9rem' }}>
              <li>A dedicated phone or SIM for your Estatel AI WhatsApp number.</li>
              <li>A ClawHosters account with your Estatel AI API key configured.</li>
            </ul>
            <p style={{ color: '#334155', lineHeight: 1.55 }}>
              Once your ClawHosters instance is running, scan the QR code in
              your ClawHosters dashboard under <em>Channels &gt; WhatsApp</em>.
              Your API key for ClawHosters setup is shown in
              <strong> Settings &gt; Integrations</strong>.
            </p>

            <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1rem',
                          flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => {
                  setShowWhatsAppGuide(false);
                  if (onItemClick) onItemClick('whatsapp');
                }}
                data-testid="whatsapp-guide-view-key"
                style={{ background: '#0073EA', color: '#fff', border: 'none',
                         padding: '0.55rem 1.1rem', borderRadius: 6,
                         cursor: 'pointer', fontWeight: 500 }}>
                View my API key
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await apiRequest('POST',
                      `${BACKEND_URL}/api/auth/setup-checklist/whatsapp-self-confirm`,
                      { body: {} });
                  } catch { /* noop */ }
                  setShowWhatsAppGuide(false);
                  await refresh();
                }}
                data-testid="whatsapp-guide-already-connected"
                style={{ background: '#fff', color: '#0073EA',
                         border: '1px solid #0073EA',
                         padding: '0.55rem 1.1rem', borderRadius: 6,
                         cursor: 'pointer', fontWeight: 500 }}>
                I have already connected
              </button>
              <button
                type="button"
                onClick={() => setShowWhatsAppGuide(false)}
                data-testid="whatsapp-guide-close"
                style={{ background: 'transparent', color: '#475569',
                         border: 'none', padding: '0.55rem 0.6rem',
                         cursor: 'pointer', fontSize: '0.85rem' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
